import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled } from '@heidi/design-system/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH4,
  TypographyP2,
  typographyVariant,
} from '@heidi/design-system/typography';
import { Check, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { YearlyOnlyPeriodTab } from '../plan-period-tabs/plan-period-tabs';
import { RecommendedSeatBadge } from '../recommended-seat-badge/recommended-seat-badge';
import { practiceSeatUpgradeCardMessages as messages } from './practice-seat-upgrade-card.messages';

/** Which plan the team is coming from; it decides the CTA and feature list. */
export type PracticeSeatUpgradeCardFromPlan = 'practice' | 'scribeTeam' | 'clinicianMax';

const FROM_PLAN_COPY: Record<
  PracticeSeatUpgradeCardFromPlan,
  {
    cta: MessageDescriptor;
    title: MessageDescriptor;
    features: readonly MessageDescriptor[];
  }
> = {
  practice: {
    cta: messages.getPracticeMax,
    title: messages.everythingInPracticePlus,
    features: [
      messages.featureMaxUsage,
      messages.featureRegularAgenticUse,
      messages.featureEarlyAccess,
    ],
  },
  scribeTeam: {
    cta: messages.getPractice,
    title: messages.everythingInScribeTeamPlus,
    features: [
      messages.featureEvidenceAnswers,
      messages.featureEvidenceSources,
      messages.featureTryAgenticWorkflows,
    ],
  },
  // Clinician Max only upgrades to Practice Max; Standard stays visible but muted.
  clinicianMax: {
    cta: messages.getPracticeMax,
    title: messages.everythingInClinicianMaxPlus,
    features: [
      messages.featureShareSessionsAndTemplates,
      messages.featureAddAssistantUsers,
      messages.featureDailyAgenticWorkflows,
    ],
  },
};

type PracticeSeatUpgradeCardSharedProps = {
  onGetPractice: () => void;
  isCtaDisabled?: boolean;
  isCtaLoading?: boolean;
  /** Explains a disabled CTA, e.g. "Contact your team admin to upgrade". */
  ctaDisabledTooltip?: string;
  /** The team's usage is high enough to recommend the Max seat. */
  isMaxSeatRecommended?: boolean;
  /**
   * `practice` (default): a Practice Standard team, so the list sells the Max
   * seat. `scribeTeam`: sells Practice over Scribe Team; both seats stay open.
   * `clinicianMax`: sells Practice Max only; Standard is shown muted.
   */
  fromPlan?: PracticeSeatUpgradeCardFromPlan;
};

export type PracticeSeatUpgradeCardProps = PracticeSeatUpgradeCardSharedProps &
  (
    | {
        /** `purchase` (default): priced, leading into the seat checkout. */
        mode?: 'purchase';
        /** Formatted per-user monthly prices, e.g. "$130". */
        standardSeatPrice: string;
        maxSeatPrice: string;
      }
    | {
        /** `request`: unpriced, for a member asking their admins to upgrade. */
        mode: 'request';
        standardSeatPrice?: never;
        maxSeatPrice?: never;
      }
  );

/** Practice plan card into the seat checkout for Practice Standard, Scribe Team, or Clinician Max. */
export function PracticeSeatUpgradeCard(props: PracticeSeatUpgradeCardProps) {
  const {
    onGetPractice,
    isCtaDisabled = false,
    isCtaLoading = false,
    ctaDisabledTooltip,
    isMaxSeatRecommended = false,
    fromPlan = 'practice',
  } = props;
  const intl = useIntl();
  const copy = FROM_PLAN_COPY[fromPlan];
  const isRequestMode = props.mode === 'request';
  const isMaxSeatRequest = isRequestMode && fromPlan === 'practice';
  const requestCta = isMaxSeatRequest
    ? messages.requestPracticeMax
    : messages.requestPractice;
  const requestCaption = isMaxSeatRequest
    ? messages.addNoteNextStep
    : messages.chooseSeatNextStep;
  const strong = (chunks: ReactNode[]) => (
    <span className={typographyVariant({ variant: 'p2Bold' })}>{chunks}</span>
  );
  const standardSeatLabel =
    props.mode === 'request'
      ? intl.formatMessage(messages.standardSeatUnpriced, { strong })
      : intl.formatMessage(messages.standardSeat, {
          price: props.standardSeatPrice,
          strong,
        });
  const maxSeatLabel =
    props.mode === 'request'
      ? intl.formatMessage(messages.maxSeatUnpriced, { strong })
      : intl.formatMessage(messages.maxSeat, { price: props.maxSeatPrice, strong });

  const cta = (
    <Button
      variant="pro"
      size="md"
      className="w-full"
      onClick={onGetPractice}
      disabled={isCtaDisabled || isCtaLoading}
      aria-busy={isCtaLoading}
    >
      {isCtaLoading ? (
        <Loader2 className="animate-spin" aria-hidden />
      ) : (
        <TierPracticeFilled size={14} aria-hidden />
      )}
      {intl.formatMessage(isRequestMode ? requestCta : copy.cta)}
    </Button>
  );

  return (
    <div className="flex w-full flex-col gap-6 rounded-xl border border-tier-pro-border bg-surface p-6">
      <div className="flex flex-col gap-4">
        <Badge size="sm" color="blue" className="self-start">
          {intl.formatMessage(messages.forCareTeams)}
        </Badge>
        <div className="flex items-center justify-between gap-4">
          <TypographyH4 className="text-text-primary">
            {intl.formatMessage(messages.practice)}
          </TypographyH4>
          {isRequestMode ? null : (
            <YearlyOnlyPeriodTab label={intl.formatMessage(messages.yearlyOnly)} />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <SeatLine
            label={standardSeatLabel}
            description={intl.formatMessage(
              isRequestMode
                ? messages.standardSeatRequestDescription
                : messages.standardSeatDescription
            )}
            badge={
              isMaxSeatRequest ? (
                <Badge size="xs" color="neutral">
                  {intl.formatMessage(messages.currentSeat)}
                </Badge>
              ) : null
            }
            disabled={isMaxSeatRequest || fromPlan === 'clinicianMax'}
          />
          <SeatLine
            label={maxSeatLabel}
            description={intl.formatMessage(
              isRequestMode
                ? messages.maxSeatRequestDescription
                : messages.maxSeatDescription
            )}
            badge={isMaxSeatRecommended ? <RecommendedSeatBadge /> : null}
          />
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        {isCtaDisabled && ctaDisabledTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block w-full">{cta}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{ctaDisabledTooltip}</TooltipContent>
          </Tooltip>
        ) : (
          cta
        )}
        <TypographyCaptionBold className="text-center text-text-secondary">
          {intl.formatMessage(
            isRequestMode ? requestCaption : messages.selectSeatsNextStep
          )}
        </TypographyCaptionBold>
      </div>
      <div className="flex flex-col gap-2">
        <TypographyCaptionBold className="text-text-primary">
          {intl.formatMessage(copy.title)}
        </TypographyCaptionBold>
        <ul className="flex flex-col gap-2">
          {copy.features.map((feature) => (
            <li key={feature.id} className="flex items-start gap-2">
              <Check className="size-4 shrink-0 text-text-primary" aria-hidden />
              <TypographyCaption className="text-text-primary">
                {intl.formatMessage(feature)}
              </TypographyCaption>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SeatLine({
  label,
  description,
  badge,
  disabled = false,
}: {
  label: ReactNode;
  description: string;
  badge: ReactNode;
  disabled?: boolean;
}) {
  const textClassName = disabled ? 'text-text-disabled' : undefined;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <TypographyP2 className={textClassName ?? 'text-text-primary'}>
          {label}
        </TypographyP2>
        {badge}
      </div>
      <TypographyCaption className={textClassName ?? 'text-text-secondary'}>
        {description}
      </TypographyCaption>
    </div>
  );
}
